import { Paper, Grid } from "@material-ui/core";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";

/*
 * Primitivas compartidas de tarjetas (dashboard, equipo, ...).
 * Lenguaje visual: superficie blanca, borde sutil, sombra neutra,
 * un único acento teal. Ver DESIGN_SYSTEM.md.
 */

export const DashboardCard = styled(Paper)(({ theme }: IStyledArguments) => ({
  background: "#ffffff",
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: 14,
  },
}));

export const DashboardCardHeader = styled("div")(
  ({ theme }: IStyledArguments) => ({
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  })
);

export const DashboardCardTitle = styled("div")(
  ({ theme }: IStyledArguments) => ({
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 600,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& svg": {
      color: theme.palette.primary.main,
      fontSize: "1.3rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  })
);

export const DashboardCardBody = styled("div")(
  ({ theme }: IStyledArguments) => ({
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  })
);

/* Badge de acento (fechas, contadores, estados) */
export const DashboardBadge = styled("span")(
  ({ theme }: IStyledArguments) => ({
    display: "inline-block",
    background: theme.palette.extra.accentSoft,
    color: theme.palette.primary.dark,
    border: "1px solid rgba(14, 124, 134, 0.2)",
    borderRadius: 8,
    padding: theme.spacing(0.5, 1.5),
    fontFamily: theme.typography.alternateFontFamily,
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: 0.3,
  })
);

/* Cabecera de sección enlazable (nombre de equipo, próximo partido…) */
export const SectionHeading = styled("h3")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "1.35rem",
  color: theme.palette.text.primary,
  margin: 0,
  padding: theme.spacing(3, 0, 2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  "& .chevron": {
    color: theme.palette.primary.main,
    transition: "transform 0.2s ease",
  },
  "&:hover .chevron": {
    transform: "translateX(4px)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.15rem",
    padding: theme.spacing(2, 0, 1.5),
  },
}));

/* Reloj de hora de entrenamiento / partido */
export const TimeWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 0),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 0),
  },
}));

export const TimeBox = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 12,
  minWidth: 80,
  height: 80,
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    minWidth: 64,
    height: 64,
  },
}));

export const TimeValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "2.5rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  lineHeight: 1,
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

export const TimeSeparator = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontSize: "2.5rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

/* Fila de jugador (asistencias, convocatorias…) */
export const PlayerRow = styled(Grid)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.25, 2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 10,
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

export const EmptyText = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  color: theme.palette.text.secondary,
  padding: theme.spacing(3, 0),
  fontSize: "0.95rem",
}));

/* ------------------------------------------------------------------ */
/* Resultado de partido (victoria/empate/derrota) — nunca un hex suelto */
/* ------------------------------------------------------------------ */
type IResult = "win" | "draw" | "loss";

const resultTokens = (theme: IStyledArguments["theme"], result: IResult) => {
  const map: Record<IResult, { color: string; background: string }> = {
    win: {
      color: theme.palette.extra.win,
      background: theme.palette.extra.winSoft,
    },
    draw: {
      color: theme.palette.extra.draw,
      background: theme.palette.extra.drawSoft,
    },
    loss: {
      color: theme.palette.extra.loss,
      background: theme.palette.extra.lossSoft,
    },
  };
  return map[result];
};

export const ResultChip = styled("span")<{ $result: IResult }>(
  ({ theme, $result }: IStyledArguments & { $result: IResult }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 8,
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "0.78rem",
    lineHeight: 1.4,
    ...resultTokens(theme, $result),
  })
);

/* Devuelve el resultado ("win"/"draw"/"loss") a partir de un marcador */
export const getMatchResult = (scoreFor: number, scoreAgainst: number): IResult => {
  if (scoreFor > scoreAgainst) return "win";
  if (scoreFor < scoreAgainst) return "loss";
  return "draw";
};

/* ------------------------------------------------------------------ */
/* Tile de estadística — número grande (Sora, tabular) + etiqueta      */
/* ------------------------------------------------------------------ */
export const StatTile = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 14,
  padding: theme.spacing(1.5, 1),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
    padding: theme.spacing(1.25, 0.5),
  },
}));

export const StatTileValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.5rem",
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.primary,
}));

export const StatTileLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.68rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

/* ------------------------------------------------------------------ */
/* Marcador grande — home / separador / away, tratado como una cifra   */
/* con el mismo peso visual que un saldo (ver DESIGN_SYSTEM.md)        */
/* ------------------------------------------------------------------ */
export const ScoreRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
}));

export const ScoreSide = styled("div")({
  flex: 1,
  minWidth: 0,
  textAlign: "center",
});

export const ScoreTeamName = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.75rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: theme.spacing(1),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const ScoreNumber = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "3rem",
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.primary,
  [theme.breakpoints.down("sm")]: {
    fontSize: "2.5rem",
  },
}));

export const ScoreSeparator = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.75rem",
  color: theme.palette.text.secondary,
}));

/* ------------------------------------------------------------------ */
/* Ticket — costura punteada con muescas, para "próximo partido" y     */
/* cualquier contenido tipo entrada. La única pieza con CSS a medida.  */
/* ------------------------------------------------------------------ */
export const TicketCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  [theme.breakpoints.down("sm")]: {
    borderRadius: 16,
  },
}));

export const TicketSection = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2.5, 3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

/* Línea punteada con dos muescas circulares "recortadas" en los bordes
 * de la card (el `overflow: hidden` de TicketCard hace de máscara). */
export const TicketSeam = styled("div")(({ theme }: IStyledArguments) => ({
  position: "relative",
  height: 1,
  borderTop: `1.5px dashed ${theme.palette.extra.borderColor}`,
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    top: -9,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: theme.palette.extra.backgroundGrey,
  },
  "&::before": {
    left: -9,
  },
  "&::after": {
    right: -9,
  },
}));
